"use strict";
// Copyright © 2021 Move Closer
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.HeroModuleUi = void 0;
const vue_property_decorator_1 = require("vue-property-decorator");
const front_1 = require("../../dsl/front");
const ui_1 = require("../_abstract/ui");
const _contracts_1 = require("../_contracts");
const _filters_1 = require("../_filters");
/**
 * @author Stanisław Gregor <stanislaw.gregor@movecloser.pl>
 */
let HeroModuleUi = class HeroModuleUi extends ui_1.AbstractModuleUi {
    constructor() {
        super(...arguments);
        /**
         * Image to be rendered in the background.
         */
        this.backgroundImage = null;
        /**
         * @private
         */
        this.imageAttrId = this.$id() + 'image';
        /**
         * @inheritDoc
         */
        this.imageRatios = {
            desktop: _contracts_1.ImageRatio.HeroDesktop,
            mobile: _contracts_1.ImageRatio.HeroMobile
        };
        /**
         * Height of the navbar (in px).
         */
        this.navbarHeight = 0;
    }
    /**
     * URL of the video to be played in the background.
     */
    get backgroundVideo() {
        if (this.module.content.backgroundObj === null ||
            this.module.content.backgroundObj.video === null) {
            return undefined;
        }
        const { desktop: desktopSrc, mobile: mobileSrc } = this.module.content.backgroundObj.video;
        if (desktopSrc === null && mobileSrc === null) {
            return undefined;
        }
        if (typeof window === 'undefined') {
            return mobileSrc !== null
                ? mobileSrc
                : desktopSrc !== null
                    ? desktopSrc
                    : undefined;
        }
        const isMobile = window.innerWidth >= 992;
        if (isMobile) {
            return mobileSrc !== null ? mobileSrc : undefined;
        }
        else {
            return desktopSrc !== null ? desktopSrc : undefined;
        }
    }
    /**
     * @inheritDoc
     */
    async fetchRelated() {
        await this.resolveBackgroundImage();
    }
    /**
     * Determines whether the module has any background media attached.
     */
    get hasBackgroundMedia() {
        return this.module.content.backgroundObj !== null;
    }
    /**
     * Handles the `@loadeddata` event on the `<video>` element.
     */
    onVideoLoadedData() {
        this.onReady();
    }
    /**
     * Scrolls to the top of the `<section id="form">`.
     */
    scrollToForm() {
        if (typeof this.scrollTarget !== 'string') {
            return;
        }
        if (this.scrollTarget[0] === '/') {
            this.$router.push(this.scrollTarget);
            return;
        }
        const scrollOffset = window.pageYOffset;
        const formEl = document.getElementById(this.scrollTarget);
        if (formEl === null)
            return;
        const formPosition = formEl.getBoundingClientRect().top;
        const top = scrollOffset + formPosition - this.navbarHeight;
        window.scrollTo({ top });
    }
    /**
     * Observes the attributes of the `.hero-module__bg-image` element
     *
     * @recursive
     */
    observeBackgroundImageAttributes() {
        if (this.module.content.backgroundObj === null) {
            this.onReady();
            return;
        }
        const backgroundImageEl = document.getElementById(this.imageAttrId);
        if (backgroundImageEl === null) {
            setTimeout(() => {
                this.observeBackgroundImageAttributes();
            }, 100);
        }
        else {
            // Create the observer.
            const observer = new MutationObserver(mutations => {
                mutations.forEach(mutation => {
                    // React only if the mutation involves the element's attributes
                    // and if the affected attribute is the `[lazy]` attribute.
                    if (mutation.type === 'attributes' && mutation.attributeName === 'lazy') {
                        // Fetch the current value of the `[lazy]` attribute.
                        const lazy = backgroundImageEl.getAttribute('lazy');
                        // If the `[lazy]` attribute has the value of `loaded`,
                        // reveal the module's contents and disconnect the observer.
                        if (lazy === 'loaded') {
                            this.onReady();
                            observer.disconnect();
                        }
                    }
                });
            });
            // Make the observer observe the target element.
            observer.observe(backgroundImageEl, {
                attributes: true
            });
        }
    }
    /**
     * ID of the element that is the target of the scroll.
     */
    get scrollTarget() {
        if (this.module.content.button === null) {
            return undefined;
        }
        return this.module.content.button.target;
    }
    /**
     * Reads the height of the navbar.
     */
    readNavbarHeight() {
        const navbar = document.querySelector('.navbar');
        this.navbarHeight = navbar !== null ? navbar.getBoundingClientRect().height : 0;
    }
    /**
     * Resolves the background image, if any.
     */
    async resolveBackgroundImage() {
        if (this.module.content.backgroundObj === null ||
            this.module.content.backgroundObj.image === null) {
            return;
        }
        let imageFile;
        try {
            imageFile = await this.relatedService.resolve(this.module.content.backgroundObj.image);
        }
        catch (e) {
            this.onError(e);
            return;
        }
        const maxWidth = this.isMobile ? 768 : 1220;
        this.backgroundImage = _filters_1.prepareImage(imageFile, this.imageRatio, maxWidth, this.module.isLazyLoaded);
    }
};
HeroModuleUi = __decorate([
    vue_property_decorator_1.Component({
        name: 'HeroModuleUi',
        components: {
            BootstrapButton: front_1.BootstrapButton,
            BootstrapImage: front_1.BootstrapImage
        },
        async created() {
            await this.fetchRelated();
        },
        mounted() {
            this.observeBackgroundImageAttributes();
        },
        template: `
    <header class="hero-module d-flex align-items-center"
            :class="{ '--no-bg-media': !hasBackgroundMedia, '--loaded': !isLoading }">

    <!-- Background video -->
    <video v-if="backgroundVideo" :src="backgroundVideo" autoplay muted playsinline webkit-playsinline
           @loadeddata="onVideoLoadedData"
           class="hero-module__bg-video" ref="video"/>

    <!-- Background image -->
    <BootstrapImage v-else-if="backgroundImage" v-bind="backgroundImage" :id="imageAttrId"
                    class="hero-module__bg-image" @load="onReady"/>

    <!-- Text content -->
    <div class="hero-module__content container">
      <div class="row justify-content-center">
        <div class="col-12 d-flex flex-column justify-content-center align-items-center">
          <!-- Heading -->
          <h1 class="hero-module__heading">
            <small v-if="module.content.textSmall" class="hero-module__heading__small"
                   v-html="module.content.textSmall"/>
            <span v-if="module.content.textLarge" v-html="module.content.textLarge"/>
          </h1>

          <!-- CTA button -->
          <BootstrapButton v-if="module.content.button" class="hero-module__button btn-cta"
                           @click="scrollToForm">
            {{ module.content.button.label }}
          </BootstrapButton>
        </div>
      </div>
    </div>
    <!-- END | Text content -->

    </header>
  `
    })
], HeroModuleUi);
exports.HeroModuleUi = HeroModuleUi;
//# sourceMappingURL=Hero.ui.js.map